import React, { useState } from 'react';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FiCalendar } from '../components/Icons';

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.colors.background};
  padding: ${(p) => p.theme.spacing.lg};
  position: absolute;
  inset: 0;
  margin: 0;
  box-sizing: border-box;
  overflow: auto;
`;

const Card = styled.div`
  background: ${(p) => p.theme.colors.surface};
  padding: ${(p) => p.theme.spacing.xxl};
  border-radius: ${(p) => p.theme.borderRadius.xl};
  box-shadow: ${(p) => p.theme.shadows.heavy};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  width: 100%;
  max-width: 420px;

  @media (max-width: 480px) {
    padding: ${(p) => p.theme.spacing.lg};
    border-radius: ${(p) => p.theme.borderRadius.large};
  }
`;

const BrandMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.text.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${(p) => p.theme.spacing.lg};
`;

const Title = styled.h1`
  text-align: center;
  margin: 0 0 ${(p) => p.theme.spacing.xs} 0;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${(p) => p.theme.colors.text.primary};
`;

const Subtitle = styled.p`
  text-align: center;
  margin: 0 0 ${(p) => p.theme.spacing.xl} 0;
  color: ${(p) => p.theme.colors.text.secondary};
  font-size: 0.95rem;
`;

// Lark's brand blue for the provider button (like "Sign in with Google").
const LarkButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(p) => p.theme.spacing.sm};
  background: #3370ff;
  color: #ffffff;
  border: none;
  padding: 13px ${(p) => p.theme.spacing.lg};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  font-family: ${(p) => p.theme.fonts.main};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #2a5fd9;
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px #3370ff40;
  }

  &:disabled {
    background: ${(p) => p.theme.colors.border.medium};
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const InfoNote = styled.div`
  margin-top: ${(p) => p.theme.spacing.xl};
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.lg};
  background: ${(p) => p.theme.colors.surfaceAlt};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.medium};

  .note-title {
    margin: 0 0 6px 0;
    font-weight: 600;
    color: ${(p) => p.theme.colors.status.info};
    font-size: 0.9rem;
  }

  .note-body {
    margin: 0;
    color: ${(p) => p.theme.colors.text.secondary};
    font-size: 0.85rem;
    line-height: 1.6;
  }
`;

const LoginPage = () => {
  const [larkLoading, setLarkLoading] = useState(false);

  const handleLarkLogin = async () => {
    setLarkLoading(true);
    try {
      const response = await authAPI.getLarkAuthUrl();
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to get Lark authentication URL');
        setLarkLoading(false);
      }
    } catch (error) {
      console.error('Lark login error:', error);
      toast.error('Failed to initiate Lark login');
      setLarkLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <BrandMark><FiCalendar size={26} /></BrandMark>
        <Title>Employee Calendar</Title>
        <Subtitle>Sign in with your Lark account to continue</Subtitle>

        <LarkButton type="button" onClick={handleLarkLogin} disabled={larkLoading}>
          {larkLoading ? (
            <>
              <Spinner />
              Redirecting to Lark…
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
              </svg>
              Sign in with Lark
            </>
          )}
        </LarkButton>

        <InfoNote>
          <p className="note-title">Authentication via Lark</p>
          <p className="note-body">
            Your Lark email must match your registered account email to access the system.
            Contact your administrator if you need access.
          </p>
        </InfoNote>
      </Card>
    </Container>
  );
};

export default LoginPage;
